import React, { useState } from "react"

import { ChevronDown } from "lucide-react"

import { CopyButton } from "@/components/CopyButton"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  AUDITED_TABLES,
  AUDIT_LOGS_DEFAULT_PAGE_SIZE,
  AuditLogAction,
  AuditLogFieldChange,
  AuditLogsFilters,
  AuditedTable,
  FRIENDLY_IDS,
  useAuditLogs,
} from "@/hooks/useAuditLogs"
import DashboardLayout from "@/layouts/DashboardLayout"
import { cn } from "@/lib/utils"
import {
  AUDIT_ACTOR_KINDS,
  AUDIT_ACTOR_KIND_LABELS,
  AuditActor,
  AuditActorKind,
  auditActorKindBadge,
  auditActorLabel,
} from "@/utils/auditActor"
import { auditValueText } from "@/utils/auditValue"

const TABLE_LABELS: Record<AuditedTable, string> = {
  people: "People",
  applications: "Applications",
  shifts: "Blocks",
  timesheets: "Timesheets",
  incentives: "Incentives",
  incentive_tickets: "Incentive tickets",
}

const recordReferencePlaceholder = (table: AuditedTable) => {
  const friendlyId = FRIENDLY_IDS[table]
  return friendlyId ? `${friendlyId.example} or row UUID` : "row UUID"
}

const EMPTY_FILTERS: AuditLogsFilters = {
  tableName: "people",
  recordReference: "",
  actor: "",
  actorKind: "any",
  action: "any",
  from: null,
  to: null,
  page: 1,
  limit: AUDIT_LOGS_DEFAULT_PAGE_SIZE,
}

const ChangeValue: React.FC<{ field: string; tone: "before" | "after"; value: unknown }> = ({
  field,
  tone,
  value,
}) => {
  const text = auditValueText(value)
  const toneClasses =
    tone === "before"
      ? "bg-red-50 text-red-900 border-red-100"
      : "bg-green-50 text-green-900 border-green-100"

  return (
    <div className="relative">
      <pre
        className={`text-xs border p-2 pr-8 rounded max-h-48 overflow-auto whitespace-pre-wrap break-words ${toneClasses}`}
      >
        {text ?? "-"}
      </pre>
      {text && (
        <CopyButton
          value={text}
          label={`${field} ${tone}`}
          className="absolute right-1 top-1 bg-white/70"
        />
      )}
    </div>
  )
}

const ActorValue: React.FC<{ label: string; value: string | null }> = ({ label, value }) => (
  <div className="flex min-w-0 items-center gap-2">
    <span
      className={cn(
        "min-w-0 flex-1 break-all font-mono text-xs",
        !value && "italic text-muted-foreground",
      )}
    >
      {value ?? "Empty"}
    </span>
    {value && <CopyButton value={value} label={label} />}
  </div>
)

// Same column widths as ChangeSet below it, so the two tables line up.
const ActorRow: React.FC<{ entry: AuditActor }> = ({ entry }) => (
  <div className="border rounded">
    <div className="grid grid-cols-12 text-xs font-semibold text-muted-foreground bg-muted/50 px-3 py-2 rounded-t">
      <div className="col-span-3">Actor</div>
      <div className="col-span-4">User ID</div>
      <div className="col-span-5">Kind</div>
    </div>
    <div className="grid grid-cols-12 gap-2 px-3 py-2 items-center">
      <div className="col-span-3">
        <ActorValue label="actor" value={entry.actor?.trim() || null} />
      </div>
      <div className="col-span-4">
        <ActorValue label="user ID" value={entry.actor_user_id} />
      </div>
      <div className="col-span-5">
        <ActorValue label="kind" value={entry.actor_kind} />
      </div>
    </div>
  </div>
)

const ChangeSet: React.FC<{ changes: Record<string, AuditLogFieldChange> }> = ({ changes }) => {
  const fields = Object.keys(changes ?? {}).sort()

  if (fields.length === 0) {
    return <p className="text-xs text-muted-foreground">No field-level changes captured.</p>
  }

  return (
    <div className="border rounded">
      <div className="grid grid-cols-12 text-xs font-semibold text-muted-foreground bg-muted/50 px-3 py-2 rounded-t">
        <div className="col-span-3">Field</div>
        <div className="col-span-4">Before</div>
        <div className="col-span-5">After</div>
      </div>
      <div className="divide-y">
        {fields.map((field) => (
          <div key={field} className="grid grid-cols-12 gap-2 px-3 py-2 items-start">
            <div className="col-span-3 font-mono text-xs break-all">{field}</div>
            <div className="col-span-4">
              <ChangeValue field={field} tone="before" value={changes[field]?.before} />
            </div>
            <div className="col-span-5">
              <ChangeValue field={field} tone="after" value={changes[field]?.after} />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

const AuditLogs: React.FC = () => {
  const [filters, setFilters] = useState<AuditLogsFilters>(EMPTY_FILTERS)
  const [openRows, setOpenRows] = useState<Record<string, boolean>>({})

  const { data, isLoading, isError, error, refetch } = useAuditLogs(filters)

  const applyFilters = () => {
    setFilters((previous) => ({ ...previous, page: 1 }))
    refetch()
  }

  const clearFilters = () => setFilters(EMPTY_FILTERS)

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-secondary">Audit Logs</h1>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Filters</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Entity</p>
                <Select
                  value={filters.tableName}
                  onValueChange={(value) =>
                    setFilters((f) => ({
                      ...f,
                      tableName: value as AuditedTable,
                      recordReference: "",
                      page: 1,
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AUDITED_TABLES.map((table) => (
                      <SelectItem key={table} value={table}>
                        {TABLE_LABELS[table]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Record</p>
                <Input
                  placeholder={recordReferencePlaceholder(filters.tableName)}
                  value={filters.recordReference || ""}
                  onChange={(e) => setFilters((f) => ({ ...f, recordReference: e.target.value }))}
                />
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Actor</p>
                <Input
                  placeholder="31845, a name, or a function"
                  value={filters.actor || ""}
                  onChange={(e) => setFilters((f) => ({ ...f, actor: e.target.value }))}
                />
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Actor type</p>
                <Select
                  value={filters.actorKind || "any"}
                  onValueChange={(value) =>
                    setFilters((f) => ({ ...f, actorKind: value as AuditActorKind | "any" }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Any" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    {AUDIT_ACTOR_KINDS.map((kind) => (
                      <SelectItem key={kind} value={kind}>
                        {AUDIT_ACTOR_KIND_LABELS[kind]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Action</p>
                <Select
                  value={filters.action || "any"}
                  onValueChange={(value) =>
                    setFilters((f) => ({ ...f, action: value as AuditLogAction | "any" }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Any" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    <SelectItem value="insert">Insert</SelectItem>
                    <SelectItem value="update">Update</SelectItem>
                    <SelectItem value="delete">Delete</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">From (ISO)</p>
                <Input
                  placeholder="2026-09-01T00:00:00Z"
                  value={filters.from || ""}
                  onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))}
                />
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">To (ISO)</p>
                <Input
                  placeholder="2026-09-30T00:00:00Z"
                  value={filters.to || ""}
                  onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
                />
              </div>
              <div className="flex items-end gap-2">
                <Button onClick={applyFilters}>Apply</Button>
                <Button variant="outline" onClick={clearFilters}>
                  Clear
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Logs</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <p className="text-sm text-muted-foreground">Loading...</p>
            ) : isError ? (
              <p className="text-sm text-red-500">
                Failed to load logs{error instanceof Error ? `: ${error.message}` : ""}
              </p>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-12 gap-2 text-sm font-semibold text-muted-foreground">
                  <div className="col-span-3">Timestamp</div>
                  <div className="col-span-3">Actor</div>
                  <div className="col-span-4">Record</div>
                  <div className="col-span-2">Action</div>
                </div>
                <div className="divide-y">
                  {data?.logs.map((log) => {
                    const isOpen = !!openRows[log.id]
                    const kindBadge = auditActorKindBadge(log)
                    return (
                      <div key={log.id} className="py-1">
                        <button
                          type="button"
                          onClick={() => setOpenRows((rows) => ({ ...rows, [log.id]: !isOpen }))}
                          className="w-full grid grid-cols-12 gap-2 items-center rounded-md px-2 py-2 text-left hover:bg-muted/50 transition-colors"
                        >
                          <div className="col-span-3">
                            {new Date(log.created_at).toLocaleString()}
                          </div>
                          <div className="col-span-3 flex min-w-0 items-center gap-2">
                            <span className="truncate">{auditActorLabel(log)}</span>
                            {kindBadge && (
                              <Badge variant="outline" className="shrink-0">
                                {kindBadge}
                              </Badge>
                            )}
                          </div>
                          <div className="col-span-4 min-w-0">
                            {data.recordLabels[log.record_id] && (
                              <p className="truncate">{data.recordLabels[log.record_id]}</p>
                            )}
                            <p className="font-mono text-xs truncate">{log.record_id}</p>
                          </div>
                          <div className="col-span-2 flex items-center justify-between">
                            <Badge variant="secondary">{log.action}</Badge>
                            <ChevronDown
                              className={`ml-2 h-4 w-4 shrink-0 transition-transform ${isOpen ? "rotate-180" : "rotate-0"}`}
                            />
                          </div>
                        </button>
                        {isOpen && (
                          <div className="mt-3 mb-4 ml-2 space-y-3">
                            <ActorRow entry={log} />
                            <ChangeSet changes={log.changes} />
                            {log.comment && (
                              <div>
                                <p className="text-xs text-muted-foreground mb-1">Comment</p>
                                <p className="text-xs">{log.comment}</p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )
                  })}
                  {data?.unresolvedReference && (
                    <p className="text-sm text-muted-foreground">
                      No {TABLE_LABELS[filters.tableName].toLowerCase()} record matches that
                      reference.
                    </p>
                  )}
                  {data && !data.unresolvedReference && data.logs.length === 0 && (
                    <p className="text-sm text-muted-foreground">No logs found</p>
                  )}
                </div>

                <div className="flex items-center justify-between pt-2">
                  <div className="text-sm text-muted-foreground">Page {filters.page}</div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      disabled={filters.page === 1}
                      onClick={() =>
                        setFilters((f) => ({ ...f, page: Math.max(1, (f.page || 1) - 1) }))
                      }
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      disabled={!data?.hasNextPage}
                      onClick={() => setFilters((f) => ({ ...f, page: (f.page || 1) + 1 }))}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  )
}

export default AuditLogs
